import { toRoundedNumber } from "@helpers";
import {
  SupportTicketAttachmentParams,
  SupportTicketAttachmentParamsContentTypeEnum,
} from "@rentablo/api/apiv1";
import {
  CONTENT_TYPE_EXTENSIONS,
  MAX_SUMMARY_LENGTH,
  SUMMARY_PREFIX,
} from "./constants";

const isAcceptedContentType = (
  type: string
): type is SupportTicketAttachmentParamsContentTypeEnum =>
  type in CONTENT_TYPE_EXTENSIONS;

const contentTypeOf = (file: File) => {
  if (isAcceptedContentType(file.type)) return file.type;

  const name = file.name.toLowerCase();

  return Object.keys(CONTENT_TYPE_EXTENSIONS).find(
    (type): type is SupportTicketAttachmentParamsContentTypeEnum =>
      isAcceptedContentType(type) &&
      CONTENT_TYPE_EXTENSIONS[type].some((extension) =>
        name.endsWith(extension)
      )
  );
};

export const attachmentKind = (file: File) => {
  const contentType = contentTypeOf(file);
  if (contentType === "application/pdf") return "PDF";
  if (contentType?.startsWith("video/")) return "VID";
  return "IMG";
};

export const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${toRoundedNumber(bytes / 1024 / 1024, 1)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;

export const buildSummary = (stepsToReproduce: string) => {
  const text = stepsToReproduce.trim().replace(/\s+/g, " ");
  const maxTextLength = MAX_SUMMARY_LENGTH - SUMMARY_PREFIX.length;

  return `${SUMMARY_PREFIX}${
    text.length > maxTextLength ? `${text.slice(0, maxTextLength - 1)}…` : text
  }`;
};

export const fileToAttachment = (
  file: File
): Promise<SupportTicketAttachmentParams> =>
  new Promise((resolve, reject) => {
    const contentType = contentTypeOf(file);
    if (!contentType) {
      reject(new Error(`Unsupported content type: ${file.type || file.name}`));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result);
      resolve({
        filename: file.name,
        contentType,
        content: result.slice(result.indexOf(",") + 1),
      });
    };
    reader.readAsDataURL(file);
  });
